/**
 * ExecutiveBriefingCard — Patch 35.5.1.
 *
 * The founder's daily executive OS card. Rendered at the top of the
 * founder homepage; consumes the canonical Akinex briefing endpoint and
 * offers Accept / Dismiss decisions that are logged to the immutable
 * ``akinex_timeline`` ledger.
 *
 * Contract (per Patch 35.5 & 35.5.1):
 *   • Deterministic renderer. No client-side ranking, rewriting, or
 *     inference of briefing content.
 *   • Section order (spec-mandated):
 *       1. Local-time greeting
 *       2. Briefing summary (headline)
 *       3. Top three ranked findings (severity → recommended action →
 *          Accept & Dismiss → expandable evidence → confidence)
 *       4. Verified recent wins (rendered ONLY when returned)
 *       5. Operational Health + Business Brain Health footer
 *   • Accept & Dismiss never mutate business records — they POST intent
 *     to /akinex/briefing/decisions, which writes to akinex_timeline.
 */
import { useMemo } from "react";
import { AlertTriangle, RefreshCw, Sparkles, Sunrise, Trophy } from "lucide-react";

import { greetingName } from "@/shared/lib/greeting";
import { useAuthStore } from "@/entities/auth/store";
import { useWorkspaceStore } from "@/app/store/workspace-store";
import {
  useAkinexBriefing,
  useAkinexDecision,
  flattenBriefingFindings,
  type AkinexBriefing,
  type AkinexFinding,
} from "@/entities/akinex/api";
import { HealthFooter } from "@/entities/akinex/HealthFooter";
import { PriorityFinding } from "@/entities/akinex/PriorityFinding";
import { Button } from "@/shared/ui/button";
import { Card } from "@/shared/ui/card";
import { Skeleton } from "@/shared/ui/skeleton";

const TOP_N = 3;

function topRankedFindings(briefing: AkinexBriefing): AkinexFinding[] {
  return flattenBriefingFindings(briefing).slice(0, TOP_N);
}

function timeOfDayGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

export function ExecutiveBriefingCard(): JSX.Element | null {
  const user = useAuthStore((s) => s.user);
  const me = useWorkspaceStore((s) => s.me);
  const currentWorkspaceId = useWorkspaceStore((s) => s.currentWorkspaceId);

  const firstName = greetingName({
    preferred_name: me?.preferred_name ?? user?.preferred_name,
    display_name: me?.display_name,
    name: user?.name,
  });
  const greeting = timeOfDayGreeting();

  const query = useAkinexBriefing(currentWorkspaceId);
  const decision = useAkinexDecision(currentWorkspaceId);

  // Guard: no workspace selected yet.
  if (!currentWorkspaceId) {
    return (
      <Card
        data-testid="akinex-briefing-card"
        data-state="no-workspace"
        className="p-5 border-white/10"
      >
        <BriefingGreeting greeting={greeting} firstName={firstName} />
        <p
          data-testid="akinex-briefing-no-workspace"
          className="mt-3 text-sm text-[var(--md-fog)]"
        >
          Select a workspace to see today&rsquo;s executive briefing.
        </p>
      </Card>
    );
  }

  // Loading state.
  if (query.isLoading) {
    return (
      <Card
        data-testid="akinex-briefing-card"
        data-state="loading"
        className="p-5 border-white/10"
      >
        <BriefingGreeting greeting={greeting} firstName={firstName} />
        <div className="mt-4 space-y-3" data-testid="akinex-briefing-loading">
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      </Card>
    );
  }

  // Error state.
  if (query.isError || !query.data) {
    return (
      <Card
        data-testid="akinex-briefing-card"
        data-state="error"
        className="p-5 border-red-500/30 bg-red-500/5"
      >
        <BriefingGreeting greeting={greeting} firstName={firstName} />
        <div className="mt-4 flex items-start gap-3">
          <AlertTriangle
            className="h-4 w-4 text-red-400 mt-0.5 shrink-0"
            aria-hidden
          />
          <div className="flex-1">
            <p
              data-testid="akinex-briefing-error"
              className="text-sm text-[var(--md-ivory-0)]"
            >
              Today&rsquo;s briefing is temporarily unavailable.
            </p>
            <p className="mt-1 text-xs text-[var(--md-fog)]">
              Akinex will retry automatically. You can also refresh manually.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => query.refetch()}
            data-testid="akinex-briefing-retry"
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" aria-hidden />
            Retry
          </Button>
        </div>
      </Card>
    );
  }

  const briefing = query.data;
  return (
    <BriefingBody
      briefing={briefing}
      greeting={greeting}
      firstName={firstName}
      onDecision={(finding, verb) => {
        decision.mutate({ finding_key: finding.key, decision: verb });
      }}
      decisionPending={decision.isPending}
    />
  );
}

function BriefingGreeting({
  greeting,
  firstName,
}: {
  greeting: string;
  firstName: string;
}): JSX.Element {
  return (
    <div>
      <div className="flex items-center gap-2">
        <Sunrise className="h-4 w-4 text-[var(--brand-royal)]" aria-hidden />
        <span
          data-testid="akinex-briefing-greeting"
          className="text-xs uppercase tracking-widest text-[var(--md-fog)]"
        >
          {greeting}, {firstName} — Executive Briefing
        </span>
      </div>
      {/* Patch 36.1 — Akinex attribution eyebrow. Subtle, non-competing. */}
      <div
        className="mt-1 akinex-eyebrow"
        data-testid="akinex-briefing-attribution"
      >
        Powered by Akinex&trade; Adaptive Intelligence
      </div>
    </div>
  );
}

function BriefingBody({
  briefing,
  greeting,
  firstName,
  onDecision,
  decisionPending,
}: {
  briefing: AkinexBriefing;
  greeting: string;
  firstName: string;
  onDecision: (finding: AkinexFinding, verb: "accepted" | "dismissed") => void;
  decisionPending: boolean;
}): JSX.Element {
  const top = useMemo(() => topRankedFindings(briefing), [briefing]);
  const wins = briefing.recent_wins ?? [];

  return (
    <Card
      data-testid="akinex-briefing-card"
      data-state={top.length === 0 ? "empty" : "populated"}
      data-attention-count={briefing.attention_count}
      className="p-5 border-white/10"
    >
      <BriefingGreeting greeting={greeting} firstName={firstName} />

      <p
        data-testid="akinex-briefing-headline"
        className="mt-3 text-lg font-semibold text-[var(--md-ivory-0)]"
      >
        {briefing.headline}
      </p>

      {top.length === 0 ? (
        <div
          data-testid="akinex-briefing-no-findings"
          className="mt-4 flex items-start gap-3 rounded-xl border border-emerald-500/25 bg-emerald-500/5 p-4"
        >
          <Sparkles
            className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0"
            aria-hidden
          />
          <div>
            <p className="text-sm font-medium text-[var(--md-ivory-0)]">
              Nothing requires your attention right now.
            </p>
            <p className="mt-1 text-xs text-[var(--md-fog)]">
              Akinex will surface the next priority as soon as it appears.
            </p>
          </div>
        </div>
      ) : (
        <ul
          data-testid="akinex-briefing-findings"
          data-count={top.length}
          className="mt-4 space-y-3"
        >
          {top.map((f) => (
            <PriorityFinding
              key={f.key}
              finding={f}
              onAccept={(finding) => onDecision(finding, "accepted")}
              onDismiss={(finding) => onDecision(finding, "dismissed")}
              disabled={decisionPending}
            />
          ))}
        </ul>
      )}

      {wins.length > 0 ? (
        <div
          data-testid="akinex-briefing-wins"
          className="mt-5 rounded-xl border border-emerald-500/25 bg-emerald-500/5 p-4"
        >
          <div className="flex items-center gap-2">
            <Trophy className="h-4 w-4 text-emerald-400" aria-hidden />
            <span className="text-[11px] uppercase tracking-widest text-emerald-400">
              Operational Highlights
            </span>
          </div>
          <ul className="mt-2 space-y-1">
            {wins.map((w, i) => (
              <li
                key={i}
                data-testid={`akinex-briefing-win-${i}`}
                className="text-sm text-[var(--md-ivory-0)]"
              >
                <span className="font-medium">{w.title}</span>
                {w.detail ? (
                  <span className="ml-1 text-[var(--md-fog)]">
                    — {w.detail}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <HealthFooter briefing={briefing} />
    </Card>
  );
}

export default ExecutiveBriefingCard;
